/**
 * useAgentPreference Hook
 *
 * Manages agent type (pro/guide), onboarding status, and chatbot position.
 * Fetches preference on mount and provides methods to update.
 */

import { useState, useCallback } from 'react';
import {
  getAgentPreference,
  saveAgentPreference,
  updateAgentPreference,
  updateChatbotPosition,
} from '../services/chatbotApi';

/**
 * @returns {Object} preference state and actions
 */
export function useAgentPreference() {
  const [agentType, setAgentType] = useState(null); // 'pro' | 'guide'
  const [onboardingCompleted, setOnboardingCompleted] = useState(null); // null = loading
  const [position, setPosition] = useState('right'); // 'right' | 'left' | 'bottom'
  const [loading, setLoading] = useState(true);

  /** Fetch current preference from API */
  const fetchPreference = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAgentPreference();
      setAgentType(data.agent_type ?? data.agentType ?? null);
      setOnboardingCompleted(data.onboarding_completed ?? data.onboardingCompleted ?? false);
      setPosition(data.chatbot_position ?? data.chatbotPosition ?? 'right');
    } catch (err) {
      // 404 means no preference yet — show onboarding
      // 401/422 means not authenticated yet — will retry after login
      if (err.status === 404 || err.status === 401 || err.status === 422) {
        setOnboardingCompleted(false);
      } else {
        console.error('Failed to fetch agent preference:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /** Save initial agent selection (onboarding) */
  const selectAgent = useCallback(async (type) => {
    try {
      await saveAgentPreference(type);
      setAgentType(type);
      setOnboardingCompleted(true);
    } catch (err) {
      console.error('Failed to save agent preference:', err);
      throw err;
    }
  }, []);

  /** Switch agent type after onboarding */
  const switchAgent = useCallback(async (type) => {
    try {
      await updateAgentPreference(type);
      setAgentType(type);
    } catch (err) {
      console.error('Failed to switch agent:', err);
      throw err;
    }
  }, []);

  /** Update chatbot panel position */
  const changePosition = useCallback(async (pos) => {
    try {
      await updateChatbotPosition(pos);
      setPosition(pos);
    } catch (err) {
      console.error('Failed to update position:', err);
      throw err;
    }
  }, []);

  // NOTE: Do NOT fetch on mount — App.jsx calls fetchPreference()
  // only when the user is authenticated. Fetching here would fire
  // before the token is available and cause 422 errors.

  return {
    agentType,
    onboardingCompleted,
    position,
    loading,
    selectAgent,
    switchAgent,
    changePosition,
    fetchPreference,
  };
}

export default useAgentPreference;
